import {IntlShape, defineMessages} from 'react-intl'

/**
 * Catálogo de Grupos de asignación (`/assignment-groups`): el listado, el modal
 * de alta/edición con sus tres pestañas y el modal de canal de Twilio.
 *
 * No se traduce:
 * - **Twilio, VICIdial, Dify, WhatsApp, SMS, Botmaker**: proveedores y canales.
 * - **Account SID / Auth Token / webhook**: términos de la API de Twilio; el ES
 *   los mantiene igual que Caller ID.
 * - **`ch.provider` y `ch.channel_type`** en crudo, los nombres de organización
 *   y de usuario, y los valores de `ring_strategy` que viajan a la API (la
 *   etiqueta sí se traduce, vía `ringStrategyLabel()`).
 * - El nombre del rol **"Supervisor"**, que es un grupo de Django.
 */
export const EM_DASH = '—'

/** La "✕" de quitar un miembro asignado: símbolo, no texto. */
export const REMOVE_MARK = '✕'

export const GROUP_MESSAGES = defineMessages({
  // ── Listado ──
  title: {id: 'telephony.groups.title', defaultMessage: 'Assignment Groups'},
  allGroups: {id: 'telephony.groups.allGroups', defaultMessage: 'All Assignment Groups'},
  newGroup: {id: 'telephony.groups.newGroup', defaultMessage: 'New Group'},
  searchPlaceholder: {
    id: 'telephony.groups.searchPlaceholder',
    defaultMessage: 'Search by group name, user name or email...',
  },
  allOrganizations: {id: 'telephony.groups.allOrganizations', defaultMessage: 'All Organizations'},
  colSupervisors: {id: 'telephony.groups.column.supervisors', defaultMessage: 'Supervisors'},
  colTelephony: {id: 'telephony.groups.column.telephony', defaultMessage: 'Telephony'},
  noOrganization: {id: 'telephony.groups.noOrganization', defaultMessage: 'No organization'},
  noSupervisors: {
    id: 'telephony.groups.noSupervisors',
    defaultMessage: 'No supervisors assigned',
  },
  membersHeading: {id: 'telephony.groups.membersHeading', defaultMessage: 'Members:'},
  noMembers: {id: 'telephony.groups.noMembers', defaultMessage: 'No members assigned'},

  // ── Telefonía del grupo ──
  visibleForTickets: {
    id: 'telephony.groups.visibleForTickets',
    defaultMessage: 'Visible for tickets',
  },
  notVisibleForTickets: {
    id: 'telephony.groups.notVisibleForTickets',
    defaultMessage: 'Not visible for tickets',
  },
  vicidialGroupTitle: {
    id: 'telephony.groups.vicidialGroupTitle',
    defaultMessage: 'VICIdial inbound group: {id}',
  },
  // Dos plurales no caben en un mismo mensaje (`no-multiple-plurals`), así que
  // el resumen del badge se arma con una llave por magnitud más el separador.
  telephonyNumbers: {
    id: 'telephony.groups.telephony.numbers',
    defaultMessage: '{count, plural, one {# number} other {# numbers}}',
  },
  telephonyAgents: {
    id: 'telephony.groups.telephony.agents',
    defaultMessage: '{count, plural, one {# agent} other {# agents}}',
  },
  telephonySummary: {
    id: 'telephony.groups.telephony.summary',
    defaultMessage: '{numbers} · {agents}',
  },
  sync: {id: 'telephony.groups.sync', defaultMessage: 'Sync'},
  syncTitle: {id: 'telephony.groups.syncTitle', defaultMessage: 'Sync members with VICIdial'},
  lastSyncTitle: {id: 'telephony.groups.lastSyncTitle', defaultMessage: 'Last sync: {date}'},
  ringStrategyTitle: {
    id: 'telephony.groups.ringStrategyTitle',
    defaultMessage: 'Call distribution strategy among the group agents',
  },
  noNumbersTitle: {
    id: 'telephony.groups.noNumbersTitle',
    defaultMessage: 'No numbers assigned to this group',
  },

  // ── Toasts del listado ──
  toastSynced: {
    id: 'telephony.groups.toast.synced',
    defaultMessage: 'Inbound group {id} synced ({agents, plural, one {# agent} other {# agents}})',
  },
  toastSkipped: {
    id: 'telephony.groups.toast.skipped',
    defaultMessage:
      '{count, plural, one {# member without a VICIdial seat} other {# members without a VICIdial seat}}: {names}',
  },
  toastSyncFailed: {
    id: 'telephony.groups.toast.syncFailed',
    defaultMessage: 'Could not sync with VICIdial',
  },
  toastSyncError: {
    id: 'telephony.groups.toast.syncError',
    defaultMessage: 'Error syncing with VICIdial',
  },

  // ── Modal de grupo ──
  addTitle: {id: 'telephony.groups.modal.addTitle', defaultMessage: 'Add Assignment Group'},
  editTitle: {id: 'telephony.groups.modal.editTitle', defaultMessage: 'Edit {name}'},
  tabGeneral: {id: 'telephony.groups.tab.general', defaultMessage: 'General Info'},
  tabChannels: {id: 'telephony.groups.tab.channels', defaultMessage: 'Communication Channels'},
  tabAi: {id: 'telephony.groups.tab.ai', defaultMessage: 'AI Integration'},
  groupNamePlaceholder: {
    id: 'telephony.groups.placeholder.groupName',
    defaultMessage: 'Enter Group name',
  },
  supervisorsOptional: {
    id: 'telephony.groups.field.supervisorsOptional',
    defaultMessage: 'Supervisors (optional)',
  },
  supervisorsEmptyHint: {
    id: 'telephony.groups.supervisorsEmptyHint',
    defaultMessage:
      'No supervisors available. Only users with "Supervisor" role from the selected organization will appear here.',
  },
  sortBy: {id: 'telephony.groups.sortBy', defaultMessage: 'Sort by:'},
  membersAvailable: {id: 'telephony.groups.membersAvailable', defaultMessage: 'Members available'},
  membersAssigned: {id: 'telephony.groups.membersAssigned', defaultMessage: 'Members assigned'},
  searchByNameOrEmail: {
    id: 'telephony.groups.searchByNameOrEmail',
    defaultMessage: 'Search by name or email...',
  },

  // ── Toasts del modal de grupo ──
  toastCreated: {
    id: 'telephony.groups.toast.created',
    defaultMessage: 'Assignment group created successfully',
  },
  toastUpdated: {
    id: 'telephony.groups.toast.updated',
    defaultMessage: 'Assignment group updated successfully',
  },
  toastUpdatedWithDify: {
    id: 'telephony.groups.toast.updatedWithDify',
    defaultMessage: 'Assignment group and Dify config updated successfully',
  },
  toastDifyNotUpdated: {
    id: 'telephony.groups.toast.difyNotUpdated',
    defaultMessage: 'Group saved, but Dify configuration failed to update',
  },
  toastDifySaveError: {
    id: 'telephony.groups.toast.difySaveError',
    defaultMessage: 'Error saving group Dify config',
  },
  toastDifySaved: {
    id: 'telephony.groups.toast.difySaved',
    defaultMessage: 'Dify configuration saved successfully',
  },
  toastDifySaveFailed: {
    id: 'telephony.groups.toast.difySaveFailed',
    defaultMessage: 'Error saving Dify config: {error}',
  },
  toastErrorLoadingOrganizations: {
    id: 'telephony.groups.toast.errorLoadingOrganizations',
    defaultMessage: 'Error loading organizations: {message}',
  },
  somethingHappened: {
    id: 'telephony.groups.somethingHappened',
    defaultMessage: 'Something happened',
  },
  unknownError: {id: 'telephony.groups.unknownError', defaultMessage: 'Unknown error'},

  // ── Pestaña de canales ──
  addChannel: {id: 'telephony.groups.channels.add', defaultMessage: 'Add Channel'},
  noChannels: {
    id: 'telephony.groups.channels.empty',
    defaultMessage: 'No channels configured yet.',
  },
  noChannelsHint: {
    id: 'telephony.groups.channels.emptyHint',
    defaultMessage: 'Click "Add Channel" to get started.',
  },
  colProvider: {id: 'telephony.groups.channels.column.provider', defaultMessage: 'Provider'},
  colAssignedMembers: {
    id: 'telephony.groups.channels.column.assignedMembers',
    defaultMessage: 'Assigned Members',
  },
  allMembers: {id: 'telephony.groups.channels.allMembers', defaultMessage: 'All members'},
  memberCount: {
    id: 'telephony.groups.channels.memberCount',
    defaultMessage: '{count, plural, one {# member} other {# members}}',
  },
  confirmDeleteChannel: {
    id: 'telephony.groups.channels.confirmDelete',
    defaultMessage: 'Delete this channel?',
  },
  adminOnly: {
    id: 'telephony.groups.channels.adminOnly',
    defaultMessage: 'Only Administrators can modify channel configuration.',
  },
  toastChannelSaved: {
    id: 'telephony.groups.channels.toast.saved',
    defaultMessage: 'Channel saved',
  },
  toastChannelDeleted: {
    id: 'telephony.groups.channels.toast.deleted',
    defaultMessage: 'Channel deleted',
  },
  toastChannelDeleteError: {
    id: 'telephony.groups.channels.toast.deleteError',
    defaultMessage: 'Error deleting channel',
  },
  errorSaveChannelFailed: {
    id: 'telephony.groups.channels.error.saveFailed',
    defaultMessage: 'Failed to save channel',
  },

  // ── Pestaña de IA ──
  loadingDify: {
    id: 'telephony.groups.dify.loading',
    defaultMessage: 'Loading Dify Configuration...',
  },
  saveDify: {id: 'telephony.groups.dify.save', defaultMessage: 'Save Dify Settings'},

  // ── Modal de canal de Twilio ──
  channelAddTitle: {id: 'telephony.groups.channel.addTitle', defaultMessage: 'Add Twilio Channel'},
  channelEditTitle: {
    id: 'telephony.groups.channel.editTitle',
    defaultMessage: 'Edit Twilio Channel',
  },
  channelType: {id: 'telephony.groups.channel.type', defaultMessage: 'Channel Type'},
  channelPhoneNumber: {
    id: 'telephony.groups.channel.phoneNumber',
    defaultMessage: 'Twilio Phone Number',
  },
  channelBotmakerLine: {
    id: 'telephony.groups.channel.botmakerLine',
    defaultMessage: 'Botmaker Phone Line',
  },
  channelMetaPhoneId: {
    id: 'telephony.groups.channel.metaPhoneId',
    defaultMessage: 'Meta Phone Number ID',
  },
  channelCredentials: {id: 'telephony.groups.channel.credentials', defaultMessage: 'Credentials'},
  channelAccountSid: {id: 'telephony.groups.channel.accountSid', defaultMessage: 'Account SID'},
  channelAuthToken: {id: 'telephony.groups.channel.authToken', defaultMessage: 'Auth Token'},
  channelAccountSidPlaceholder: {
    id: 'telephony.groups.channel.accountSidPlaceholder',
    defaultMessage: 'Your Twilio Account SID',
  },
  channelAuthTokenPlaceholder: {
    id: 'telephony.groups.channel.authTokenPlaceholder',
    defaultMessage: 'Your Twilio Auth Token',
  },
  channelAssignedMembersHint: {
    id: 'telephony.groups.channel.assignedMembersHint',
    defaultMessage: 'Empty = all group members. Select to assign to specific members only.',
  },
  channelSelectMembers: {
    id: 'telephony.groups.channel.selectMembers',
    defaultMessage: 'Select members...',
  },
  channelWebhookUrl: {id: 'telephony.groups.channel.webhookUrl', defaultMessage: 'Webhook URL'},
  channelWebhookUrlLabel: {
    id: 'telephony.groups.channel.webhookUrlLabel',
    defaultMessage: 'Webhook URL (Inbound Messages)',
  },
  channelWebhookHint: {
    id: 'telephony.groups.channel.webhookHint',
    defaultMessage: 'Configure this URL in your {provider} webhook settings for incoming messages',
  },
  channelStatusUrl: {
    id: 'telephony.groups.channel.statusUrl',
    defaultMessage: 'Status Callback URL',
  },
  channelStatusUrlLabel: {
    id: 'telephony.groups.channel.statusUrlLabel',
    defaultMessage: 'Status Callback URL (Delivery Receipts)',
  },
  channelStatusHint: {
    id: 'telephony.groups.channel.statusHint',
    defaultMessage:
      'Configure this URL in your Twilio webhook settings for delivery status updates (✓✓)',
  },
  channelSave: {id: 'telephony.groups.channel.save', defaultMessage: 'Save Channel'},
  channelMissingRequired: {
    id: 'telephony.groups.channel.missingRequired',
    defaultMessage: 'Missing required fields: {fields}',
  },
  channelMissingFields: {
    id: 'telephony.groups.channel.missingFields',
    defaultMessage: 'Missing fields: {fields}',
  },
  channelTestOk: {
    id: 'telephony.groups.channel.testOk',
    defaultMessage: 'Credentials verified successfully! Account: {account}',
  },
  channelTestFailed: {
    id: 'telephony.groups.channel.testFailed',
    defaultMessage: 'Failed to verify credentials. Please check and try again.',
  },
  toastCopied: {
    id: 'telephony.groups.channel.toast.copied',
    defaultMessage: '{label} copied to clipboard!',
  },
  toastCopyFailed: {
    id: 'telephony.groups.channel.toast.copyFailed',
    defaultMessage: 'Failed to copy to clipboard',
  },

  // ── Reusadas de common.* / validation.* ──
  nameRequired: {id: 'validation.name.required', defaultMessage: 'Name is required'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  copy: {id: 'common.action.copy', defaultMessage: 'Copy'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  selectOption: {id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'},
  colName: {id: 'common.field.name', defaultMessage: 'Name'},
  colEmail: {id: 'common.field.email', defaultMessage: 'Email'},
  colMembers: {id: 'common.field.members', defaultMessage: 'Members'},
  colCreated: {id: 'common.field.created', defaultMessage: 'Created'},
  colActions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  colType: {id: 'common.field.type', defaultMessage: 'Type'},
  colStatus: {id: 'common.field.status', defaultMessage: 'Status'},
  colOrganization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  colPhoneNumber: {id: 'common.field.phoneNumber', defaultMessage: 'Phone Number'},
  statusActive: {id: 'common.status.active', defaultMessage: 'Active'},
  statusInactive: {id: 'common.status.inactive', defaultMessage: 'Inactive'},
})

/**
 * Estrategias de reparto de llamadas de VICIdial. El `value` viaja a la API; lo
 * que se traduce es la etiqueta.
 */
const RING_STRATEGY = defineMessages({
  longest_wait_time: {
    id: 'telephony.groups.ringStrategy.longestWaitTime',
    defaultMessage: 'Longest waiting agent',
  },
  fewest_calls: {
    id: 'telephony.groups.ringStrategy.fewestCalls',
    defaultMessage: 'Fewest calls today',
  },
  oldest_call_finish: {
    id: 'telephony.groups.ringStrategy.idleLongest',
    defaultMessage: 'Idle longest',
  },
  ring_all: {id: 'telephony.groups.ringStrategy.ringAll', defaultMessage: 'Ring all'},
  random: {id: 'telephony.groups.ringStrategy.random', defaultMessage: 'Random'},
})

export const RING_STRATEGY_VALUES = Object.keys(RING_STRATEGY) as Array<keyof typeof RING_STRATEGY>

/** Una estrategia añadida en el backend y aún sin etiqueta se muestra en crudo. */
export const ringStrategyLabel = (intl: IntlShape, value: string): string => {
  const descriptor = RING_STRATEGY[value as keyof typeof RING_STRATEGY]
  return descriptor ? intl.formatMessage(descriptor) : value
}

/**
 * Tipo de canal en el selector. `WHATSAPP` se muestra con el casing de la marca;
 * el resto llega en crudo del backend y no se traduce.
 */
export const channelTypeLabel = (type: string) => (type === 'WHATSAPP' ? 'WhatsApp' : type)
